import { type Metadata, type MetadataValue, StyleType } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createSettingsParam,
  createSettingsParams,
  type ISettingsParam,
  SettingsParamType,
} from '@wix/tpa-settings';
import { AccessibilityHtmlTags } from '../../utils/AccessibilityHtmlTags.const';
import {
  AlignmentOptions,
  GalleryArrowPositionOptions,
  GalleryItemSizeOptions,
  GalleryLayoutOptions,
  ImagePositionOptions,
  ImageResizeOptions,
  type ISection,
  SectionTypes,
  SidebarPosition,
} from './types';

export const defaultSections: ISection[] = [
  {
    type: SectionTypes.TITLE_TAGLINE,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.DETAILS,
    visible: true,
    bookButton: true,
  },
  {
    type: SectionTypes.DESCRIPTION,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.GALLERY,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.SCHEDULING,
    visible: true,
    bookButton: true,
  },
  {
    type: SectionTypes.POLICY,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.CONTACT,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.PLUGIN,
    visible: true,
    bookButton: false,
  },
];

interface DesktopSettingsKeys {
  policySectionTitle: SettingsParamType.Text;
  sections: ISection[];
  headerVisibility: SettingsParamType.Boolean;
  headerTitleVisibility: SettingsParamType.Boolean;
  headerTitleAlignment: AlignmentOptions;
  headerImageVisibility: SettingsParamType.Boolean;
  headerImagePosition: ImagePositionOptions;
  headerBookButtonVisibility: SettingsParamType.Boolean;
  sidebarVisibility: SettingsParamType.Boolean;
  onlineBadgeVisibility: SettingsParamType.Boolean;
  onlineBadgeText: SettingsParamType.Text;
  serviceTitleVisibility: SettingsParamType.Boolean;
  serviceTaglineVisibility: SettingsParamType.Boolean;
  detailsDurationVisibility: SettingsParamType.Boolean;
  detailsPriceVisibility: SettingsParamType.Boolean;
  detailsLocationVisibility: SettingsParamType.Boolean;
  detailsBoxAlignment: AlignmentOptions | undefined;
  detailsTextAlignment: AlignmentOptions | undefined;
  detailsButtonAlignment: AlignmentOptions | undefined;
  titleAndTaglineAlignment: AlignmentOptions | undefined;
  businessEmailVisibility: SettingsParamType.Boolean;
  businessPhoneNumberVisibility: SettingsParamType.Boolean;
  businessAddressVisibility: SettingsParamType.Boolean;
  locationNameVisibility: SettingsParamType.Boolean;
  contactDetailsTitleText: SettingsParamType.Text;
  descriptionTitleText: SettingsParamType.Text;
  bookButtonText: SettingsParamType.Text;
  pendingApprovalButtonText: SettingsParamType.Text;
  serviceUnavailableMessageText: SettingsParamType.Text;
  availabilityMessageText: SettingsParamType.Text;
  numberOfSpotsLeftMessageText: SettingsParamType.Text;
  fullCourseMessageText: SettingsParamType.Text;
  unBookableCourseMessageText: SettingsParamType.Text;
  bookStartedCourseMessageText: SettingsParamType.Text;
  sidebarSection: SectionTypes;
  sidebarFreezePosition: SettingsParamType.Boolean;
  sidebarPosition: SidebarPosition;
  sidebarAlignment: AlignmentOptions;
  bodyAlignment: AlignmentOptions;
  columnPosition: AlignmentOptions;
  scheduleDays: SettingsParamType.Number;
  scheduleSectionTitle: SettingsParamType.Text;
  scheduleLoadAllText: SettingsParamType.Text;
  scheduleEmptyStateText: SettingsParamType.Text;
  scheduleDurationVisibility: SettingsParamType.Boolean;
  scheduleStaffVisibility: SettingsParamType.Boolean;
  showSessionLimit: SettingsParamType.Boolean;
  scheduleSessionLimit: SettingsParamType.Number;
  headerTitleHtmlTag: SettingsParamType.String;
  bodyServiceTitleHtmlTag: SettingsParamType.String;
  bodySectionTitleHtmlTag: SettingsParamType.String;
  sidebarServiceTitleHtmlTag: SettingsParamType.String;
  sidebarTitleHtmlTag: SettingsParamType.String;
  galleryLayout: GalleryLayoutOptions;
  galleryMasonryItemSpacing: SettingsParamType.Number;
  galleryMasonryItemBorderWidth: SettingsParamType.Number;
  galleryMasonryItemRadius: SettingsParamType.Number;
  galleryMasonryItemSize: GalleryItemSizeOptions;
  galleryGridItemSpacing: SettingsParamType.Number;
  galleryGridItemBorderWidth: SettingsParamType.Number;
  galleryGridItemRadius: SettingsParamType.Number;
  galleryGridThumbnailSize: SettingsParamType.Number;
  gallerySliderItemSpacing: SettingsParamType.Number;
  gallerySliderItemBorderWidth: SettingsParamType.Number;
  gallerySliderItemRadius: SettingsParamType.Number;
  gallerySliderImageResizeOption: ImageResizeOptions;
  gallerySliderLoopImages: SettingsParamType.Boolean;
  gallerySliderAutoSlide: SettingsParamType.Boolean;
  gallerySliderTimeBetweenImages: SettingsParamType.Number;
  gallerySliderShowArrows: SettingsParamType.Boolean;
  gallerySliderArrowsSize: SettingsParamType.Number;
  gallerySliderArrowsPosition: GalleryArrowPositionOptions;
  gallerySliderHeight: SettingsParamType.Number;
  buttonsFullWidth: SettingsParamType.Boolean;
  courseAvailability: SettingsParamType.Boolean;
  displayNumberOfSpots: SettingsParamType.Boolean;
}

interface MobileSettingsKeys {
  mobileSections: SectionTypes[] | undefined;
}

export const uninitializedVisibilitySettingsFalseValue = -1 as any as boolean;
export const uninitializedVisibilitySettingsTrueValue = -2 as any as boolean;

const getMetadata = <T extends MetadataValue>(defaultValue: T): Metadata<T> => ({
  type: StyleType.UNKNOWN_CssPropertyType,
  defaultValue: {
    type: 'static',
    data: defaultValue,
  },
});

const settingsParam = <T extends MetadataValue>(defaultValue: T) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(defaultValue),
});

const settingsParamWithUndefinedDefault = <T extends MetadataValue>() => ({
  getDefaultValue: () => undefined as unknown as T,
  metadata: getMetadata(undefined as unknown as T),
});

const typedSettingsParam = <Type extends SettingsParamType, T extends MetadataValue>(
  type: Type | undefined,
  defaultValue: T,
) => ({
  type,
  ...settingsParam(defaultValue),
});

const settingsParamWithoutInheritedDesktop = <T extends MetadataValue>(defaultValue: T) => ({
  inheritDesktop: false,
  ...settingsParam(defaultValue),
});

const settingsParamWithMobileDefault = <T extends MetadataValue>(
  mobileDefaultValue: T,
  defaultValue: T,
) => ({
  inheritDesktop: false,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  metadata: getMetadata(defaultValue),
});

const settingsParamWithRTLDefault = <T>(
  rtlDefaultValue: T,
  ltrDefaultValue: T,
) => ({
  getDefaultValue: ({ isRTL }) => (isRTL ? rtlDefaultValue : ltrDefaultValue),
  // metadata: getMetadata(defaultValue), // TODO: Fix metadata
});

const textSettingsParam = (defaultValueKey: string) => ({
  getDefaultValue: ({ t }) => t(defaultValueKey),
  type: SettingsParamType.Text,
  // metadata: getMetadata(defaultValue), // TODO: Fix metadata
});

const scheduleDays = createSettingsParam(
  'scheduleDays',
  typedSettingsParam(SettingsParamType.Number, 7),
) as ISettingsParam;

export default createSettingsParams<DesktopSettingsKeys & MobileSettingsKeys>({
  sections: typedSettingsParam(SettingsParamType.Object, defaultSections),
  headerVisibility: settingsParam(true),
  headerTitleVisibility: settingsParam(false),
  headerTitleAlignment: settingsParam(AlignmentOptions.CENTER),
  headerImageVisibility: settingsParam(true),
  headerImagePosition: settingsParam(ImagePositionOptions.MIDDLE),
  headerBookButtonVisibility: settingsParam(false),
  sidebarVisibility: settingsParam(false),
  sidebarSection: settingsParam(SectionTypes.CONTACT),
  sidebarFreezePosition: settingsParam(false),
  sidebarPosition: settingsParamWithRTLDefault(
    SidebarPosition.LEFT,
    SidebarPosition.RIGHT,
  ),
  sidebarAlignment: settingsParamWithRTLDefault(
    AlignmentOptions.RIGHT,
    AlignmentOptions.LEFT,
  ),
  onlineBadgeVisibility: settingsParam(true),
  serviceTitleVisibility: settingsParam(true),
  serviceTaglineVisibility: settingsParam(true),
  titleAndTaglineAlignment: settingsParamWithUndefinedDefault<AlignmentOptions>(),
  policySectionTitle: textSettingsParam(
    'app.settings.sections.policy.default.title',
  ),
  detailsDurationVisibility: settingsParam(true),
  detailsPriceVisibility: settingsParam(true),
  detailsLocationVisibility: settingsParam(true),
  detailsBoxAlignment: settingsParamWithUndefinedDefault<AlignmentOptions>(),
  detailsTextAlignment: settingsParamWithUndefinedDefault<AlignmentOptions>(),
  detailsButtonAlignment: settingsParamWithUndefinedDefault<AlignmentOptions>(),
  businessEmailVisibility: settingsParam(true),
  businessPhoneNumberVisibility: settingsParam(true),
  businessAddressVisibility: settingsParam(true),
  locationNameVisibility: settingsParam(false),
  contactDetailsTitleText: textSettingsParam(
    'app.settings.sections.contact.default.contact-details-title.label',
  ),
  descriptionTitleText: textSettingsParam('app.description.title'),
  onlineBadgeText: textSettingsParam(
    'app.settings.sections.titles.default.online-badge.text',
  ),
  bookButtonText: textSettingsParam('app.widget.cta.book-now'),
  pendingApprovalButtonText: textSettingsParam(
    'app.widget.cta.pending-approval',
  ),
  serviceUnavailableMessageText: textSettingsParam(
    'app.widget.uou-messages.book-not-available',
  ),
  availabilityMessageText: textSettingsParam('service.course.available-spots'),
  numberOfSpotsLeftMessageText: typedSettingsParam(SettingsParamType.Text, ''),
  fullCourseMessageText: textSettingsParam(
    'app.widget.uou-messages.fully-booked',
  ),
  unBookableCourseMessageText: textSettingsParam(
    'app.widget.uou-messages.book-already-closed',
  ),
  bookStartedCourseMessageText: textSettingsParam(
    'app.widget.uou-messages.book-available-when-service-started',
  ),
  bodyAlignment: settingsParamWithRTLDefault(
    AlignmentOptions.RIGHT,
    AlignmentOptions.LEFT,
  ),
  columnPosition: settingsParam(AlignmentOptions.CENTER),
  scheduleDays,
  scheduleSectionTitle: textSettingsParam('app.scheduling.title.default'),
  scheduleLoadAllText: textSettingsParam(
    'app.scheduling.load-all-sessions.default',
  ),
  scheduleEmptyStateText: {
    getDefaultValue: ({ t, getSettingParamValue }) =>
      t('app.scheduling.empty-state', {
        daysAhead: getSettingParamValue(scheduleDays),
      }),
    type: SettingsParamType.Text,
  },
  scheduleDurationVisibility: settingsParam(true),
  scheduleStaffVisibility: settingsParam(true),
  showSessionLimit: settingsParam(true),
  scheduleSessionLimit: settingsParam(3),
  headerTitleHtmlTag: settingsParam(AccessibilityHtmlTags.MainHeader),
  sidebarServiceTitleHtmlTag: settingsParam(AccessibilityHtmlTags.MainHeader),
  bodyServiceTitleHtmlTag: settingsParam(AccessibilityHtmlTags.MainHeader),
  bodySectionTitleHtmlTag: settingsParam(AccessibilityHtmlTags.SecondaryHeader),
  sidebarTitleHtmlTag: settingsParam(AccessibilityHtmlTags.SecondaryHeader),
  galleryLayout: settingsParam(GalleryLayoutOptions.GRID),
  galleryMasonryItemSize: settingsParam(GalleryItemSizeOptions.MEDIUM),
  galleryMasonryItemSpacing: settingsParamWithoutInheritedDesktop(12),
  galleryMasonryItemBorderWidth: settingsParam(0),
  galleryMasonryItemRadius: settingsParam(0),
  galleryGridThumbnailSize: settingsParamWithMobileDefault(200, 280),
  galleryGridItemSpacing: settingsParamWithoutInheritedDesktop(12),
  galleryGridItemBorderWidth: settingsParam(0),
  galleryGridItemRadius: settingsParam(0),
  gallerySliderItemSpacing: settingsParamWithoutInheritedDesktop(12),
  gallerySliderItemBorderWidth: settingsParam(0),
  gallerySliderItemRadius: settingsParam(0),
  gallerySliderImageResizeOption: settingsParam(ImageResizeOptions.FIT),
  gallerySliderTimeBetweenImages: settingsParam(4),
  gallerySliderLoopImages: settingsParam(true),
  gallerySliderAutoSlide: settingsParam(false),
  gallerySliderShowArrows: settingsParam(true),
  gallerySliderArrowsSize: settingsParamWithMobileDefault(18, 16),
  gallerySliderArrowsPosition: settingsParamWithMobileDefault(
    GalleryArrowPositionOptions.INSIDE,
    GalleryArrowPositionOptions.OUTSIDE,
  ),
  gallerySliderHeight: settingsParamWithMobileDefault(200, 400),
  buttonsFullWidth: settingsParamWithoutInheritedDesktop(false),
  mobileSections: typedSettingsParam(SettingsParamType.Object, undefined as any),
  courseAvailability: settingsParam(true),
  displayNumberOfSpots: settingsParam(false),
});
